import React from 'react'
import { Button, Card, Flex, List, Typography } from 'antd'
import { ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DashboardAttention as Attention } from '../dashboard'
import { Site } from '../../site/site'
import { hasTodo, isError, useIssue } from '../../site/components/sites-overview'

type DashboardAttentionProps = {
    attention: Attention[]
    /** The websites of the company, whose to-dos show here too. */
    sites: Site[]
}

type Tone = 'error' | 'warning' | 'info'

const ICONS: Record<Tone, React.ReactNode> = {
    error: <ExclamationCircleFilled className="text-red-500" />,
    warning: <ExclamationCircleFilled className="text-orange-500" />,
    info: <InfoCircleFilled className="text-blue-500" />,
}

/**
 * What the customer has to do, the most pressing first, each with a link to where it is done. Only problems and
 * tasks: good news, like good matches, shows elsewhere. Without to-dos the card does not show: the list does not
 * check everything yet, so an empty list does not mean that all is well.
 */
export function DashboardAttention({ attention, sites }: DashboardAttentionProps) {
    const { t } = useTranslation()
    const sitesToDo = sites.filter(hasTodo)

    if (attention.length === 0 && sitesToDo.length === 0) {
        return null
    }

    return (
        <Card title={t('Needs your attention')}>
            <List split>
                {attention.filter(isPlanItem).map((item) => (
                    <AttentionItem
                        key={item.kind}
                        item={item}
                    />
                ))}
                {sitesToDo.map((site) => (
                    <SiteToDo
                        key={site.id}
                        site={site}
                    />
                ))}
                {attention
                    .filter((item) => !isPlanItem(item))
                    .map((item) => (
                        <AttentionItem
                            key={attentionKey(item)}
                            item={item}
                        />
                    ))}
            </List>
        </Card>
    )
}

function isPlanItem(item: Attention) {
    return item.kind === 'trial_ending' || item.kind === 'candidates_almost_used' || item.kind === 'candidates_used_up'
}

function attentionKey(item: Attention) {
    switch (item.kind) {
        case 'campaign_blocked':
            return `${item.kind}-${item.campaignId}`
        case 'unanswered_questions':
            return `${item.kind}-${item.siteId}`
        default:
            return item.kind
    }
}

function Row({ tone, text, action }: { tone: Tone; text: string; action?: React.ReactNode }) {
    return (
        <List.Item extra={action}>
            <Flex
                gap="small"
                align="baseline"
            >
                {ICONS[tone]}
                <Typography.Text>{text}</Typography.Text>
            </Flex>
        </List.Item>
    )
}

function SiteToDo({ site }: { site: Site }) {
    const issue = useIssue(site)

    if (!issue) {
        return null
    }

    return (
        <Row
            tone={isError(site) ? 'error' : 'warning'}
            text={`${site.name}: ${issue.text}`}
            action={issue.action}
        />
    )
}

function AttentionItem({ item }: { item: Attention }) {
    const { t } = useTranslation()

    switch (item.kind) {
        case 'trial_ending':
            return (
                <Row
                    tone="info"
                    text={t('Your free trial ends on {{date}}. After that, your plan starts.', {
                        date: dayjs(item.endsAt).format('D MMMM'),
                    })}
                    action={
                        <Link to="/settings">
                            <Button size="small">{t('View plan')}</Button>
                        </Link>
                    }
                />
            )
        case 'candidates_almost_used':
            return (
                <Row
                    tone="warning"
                    text={
                        item.trial
                            ? t('{{usage}} of the {{limit}} candidates of your free trial are used.', item)
                            : t(
                                  '{{usage}} of the {{limit}} candidates of this billing period are used. After that, no new candidates come in.',
                                  item
                              )
                    }
                    action={
                        <Link to="/settings">
                            <Button size="small">{t('View plan')}</Button>
                        </Link>
                    }
                />
            )
        case 'candidates_used_up':
            return (
                <Row
                    tone="error"
                    text={t(
                        'All {{limit}} candidates of this billing period are used. No new candidates come in until you turn on extra candidates.',
                        item
                    )}
                    action={
                        <Link to="/settings">
                            <Button size="small">{t('Turn on')}</Button>
                        </Link>
                    }
                />
            )
        case 'campaign_blocked':
            return (
                <Row
                    tone="error"
                    text={t('{{campaign}} is on hold: WhatsApp has not approved the start message yet.', item)}
                    action={
                        <Link
                            to="/campaigns/$campaignId/settings/whatsapp"
                            params={{ campaignId: item.campaignId }}
                        >
                            <Button size="small">{t('View')}</Button>
                        </Link>
                    }
                />
            )
        case 'unanswered_questions':
            return (
                <Row
                    tone="warning"
                    text={t('Evaa could not answer {{count}} questions on {{site}}.', item)}
                    action={
                        <Link
                            to="/websites/$siteId"
                            params={{ siteId: item.siteId }}
                            search={{ view: 'questions' }}
                        >
                            <Button size="small">{t('Answer')}</Button>
                        </Link>
                    }
                />
            )
    }
}
